import { useState, useCallback, useMemo } from 'react';
import { apiClient } from '../../../config/api-config';
import {
  useApiMutation,
  useFileUpload,
  useApiQuery,
  useInfiniteApiQuery,
} from '@tasama/api-client-query';
import { useTranslation } from '@tasama/i18n';
import type {
  TicketDetailResponse,
  CreateTicketResponse,
  UploadAttachmentsResponse,
  AddCommentRequest,
  AddCommentResponse,
  TicketsListResponse,
  DropdownOptionsResponse,
  SupportIssuesCatalogResponse,
} from '../types';
import { useSupportTicketsStore } from '../store';

/**
 * Support Tickets API Endpoints
 * Centralized endpoint configuration for support tickets operations
 */
export const SUPPORT_TICKETS_ENDPOINTS = {
  BASE: 'support-ticketing',
  OPEN: 'support-ticketing/open-tickets',
  CLOSED: 'support-ticketing/closed-tickets',
  CREATE: 'support-ticketing/tickets',
  DETAIL: (ticketId: string) => `support-ticketing/tickets/detail/${ticketId}`,
  UPLOAD_ATTACHMENTS: () => `support-ticketing/tickets/attachments`,
  SEARCH: 'support-ticketing/tickets/search',
  SERVICES: 'support-ticketing/tickets/services',
  ADD_COMMENT: (ticketId: string) =>
    `support-ticketing/tickets/${ticketId}/comments`,
  REOPEN: (ticketId: string) => `support-ticketing/tickets/${ticketId}/reopen`,
  MARK_READ: (ticketId: string) =>
    `support-ticketing/tickets/${ticketId}/mark-read`,
  DROPDOWN_OPTIONS: 'support-ticketing/dropdown-options',
  SUPPORT_ISSUES: 'support-ticketing/support-issues',
} as const;

/** TDF-1351: Service filter option (L3 service the account has tickets against) */
export interface TicketServiceOption {
  id: string;
  name: string;
}

/**
 * Custom hooks for Support Tickets API operations
 * Uses React Query for data fetching with built-in caching and error handling
 *
 * NOTE: All hooks use React Query's caching, so multiple components
 * calling the same hook will share the same cached data and only make
 * one API call
 */

// ============================================================================
// QUERY HOOKS - INFINITE SCROLL
// ============================================================================

/**
 * Hook for fetching open tickets with infinite scroll support
 * Uses React Query's useInfiniteQuery for automatic pagination
 * @param pageSize - Number of tickets to fetch per page (default: 10)
 * @param srAttemptId - Optional SR attempt UUID to filter tickets (service details tab)
 * @param enabled - Whether to enable the query (default: true)
 * @param topic - Optional topic filter (TDF-1351)
 * @param serviceId - Optional L3 service filter (TDF-1351)
 * @param keyword - Optional free-text search over number/topic/subject (TDF-1351 AC7)
 */
export const useInfiniteOpenTicketsQuery = (
  pageSize: number = 10,
  srAttemptId: string | undefined,
  enabled: boolean = true,
  topic?: string | undefined,
  serviceId?: string | undefined,
  keyword?: string | undefined,
  status?: string | undefined,
  bundleAttemptId?: string | undefined
) => {
  return useInfiniteApiQuery<TicketsListResponse, TicketsListResponse>(
    [
      'support-tickets',
      'open',
      'infinite',
      String(pageSize),
      ...(srAttemptId !== undefined ? [srAttemptId] : []),
      topic ?? '',
      serviceId ?? '',
      keyword ?? '',
      status ?? '',
      bundleAttemptId ?? '',
    ],
    {
      getEndpoint: (pageParam: number) => {
        const queryParams = new URLSearchParams({
          offset: pageParam.toString(),
          size: pageSize.toString(),
        });
        if (srAttemptId) queryParams.set('srAttemptId', srAttemptId);
        // TDF-1342: bundle-side equivalent of srAttemptId — the embedded
        // bundle-detail Support Tickets tab.
        if (bundleAttemptId)
          queryParams.set('bundleAttemptId', bundleAttemptId);
        if (serviceId) queryParams.set('serviceId', serviceId);
        if (topic) queryParams.set('topic', topic);
        if (keyword) queryParams.set('keyword', keyword);
        // TDF-1394: Status filter — Open tab only, third control alongside Topic/Service
        if (status) queryParams.set('status', status);
        return `${SUPPORT_TICKETS_ENDPOINTS.OPEN}?${queryParams.toString()}`;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        // TDF-1351: offset is now a true cumulative row offset (backend fix),
        // not an incrementing page number — mirrors useInfiniteTeamsQuery.
        const totalLoaded = allPages.reduce(
          (sum, page) => sum + page.tickets.length,
          0
        );
        return totalLoaded < lastPage.total_count ? totalLoaded : undefined;
      },
      staleTime: 30 * 1000, // 30 seconds
      retry: 3,
      retryDelay: 1000,
      enabled, // Control whether the query should run
    }
  );
};

/**
 * Hook for fetching closed tickets with infinite scroll support
 * Uses React Query's useInfiniteQuery for automatic pagination
 * @param pageSize - Number of tickets to fetch per page (default: 10)
 * @param srAttemptId - Optional SR attempt UUID to filter tickets (service details tab)
 * @param enabled - Whether to enable the query (default: true)
 * @param topic - Optional topic filter (TDF-1351)
 * @param serviceId - Optional L3 service filter (TDF-1351)
 * @param keyword - Optional free-text search over number/topic/subject (TDF-1351 AC7)
 */
export const useInfiniteClosedTicketsQuery = (
  pageSize: number = 10,
  srAttemptId: string | undefined,
  enabled: boolean = true,
  topic?: string | undefined,
  serviceId?: string | undefined,
  keyword?: string | undefined,
  bundleAttemptId?: string | undefined
) => {
  return useInfiniteApiQuery<TicketsListResponse, TicketsListResponse>(
    [
      'support-tickets',
      'closed',
      'infinite',
      String(pageSize),
      ...(srAttemptId !== undefined ? [srAttemptId] : []),
      topic ?? '',
      serviceId ?? '',
      keyword ?? '',
      bundleAttemptId ?? '',
    ],
    {
      getEndpoint: (pageParam: number) => {
        const queryParams = new URLSearchParams({
          offset: pageParam.toString(),
          size: pageSize.toString(),
        });
        if (srAttemptId) queryParams.set('srAttemptId', srAttemptId);
        if (bundleAttemptId)
          queryParams.set('bundleAttemptId', bundleAttemptId);
        if (serviceId) queryParams.set('serviceId', serviceId);
        if (topic) queryParams.set('topic', topic);
        if (keyword) queryParams.set('keyword', keyword);
        return `${SUPPORT_TICKETS_ENDPOINTS.CLOSED}?${queryParams.toString()}`;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        // TDF-1351: offset is now a true cumulative row offset (backend fix),
        // not an incrementing page number — mirrors useInfiniteTeamsQuery.
        const totalLoaded = allPages.reduce(
          (sum, page) => sum + page.tickets.length,
          0
        );
        return totalLoaded < lastPage.total_count ? totalLoaded : undefined;
      },
      staleTime: 30 * 1000, // 30 seconds
      retry: 3,
      retryDelay: 1000,
      enabled, // Control whether the query should run
    }
  );
};

/**
 * Hook for fetching the distinct services the account has tickets against
 * (TDF-1351 Service filter type-ahead), scoped to the open/closed tab.
 * @param active - Optional tab filter (true=Open, false=Closed, omitted=both)
 * @param enabled - Whether to enable the query (default: true)
 */
export const useTicketServiceOptions = (
  active?: boolean | undefined,
  enabled: boolean = true
) => {
  const queryParams = new URLSearchParams();
  if (active !== undefined) {
    queryParams.append('active', active.toString());
  }

  return useApiQuery<TicketServiceOption[]>(
    [
      'support-tickets',
      'service-options',
      active === undefined ? 'all' : String(active),
    ],
    `${SUPPORT_TICKETS_ENDPOINTS.SERVICES}?${queryParams.toString()}`,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
      enabled,
    } as any
  );
};

/**
 * Hook for searching tickets across all statuses
 * @param searchQuery - Search keyword
 * @param page - Page number (0-based)
 * @param pageSize - Number of items per page
 */
export const useSearchTickets = (
  searchQuery?: string,
  page?: number,
  pageSize?: number,
  active?: boolean
) => {
  // Build query parameters
  const queryParams = new URLSearchParams();

  if (searchQuery) {
    queryParams.append('keyword', searchQuery);
  }
  if (active !== undefined) {
    queryParams.append('active', active.toString());
  }
  const _page = page === 0 ? '0' : page;
  if (_page !== undefined) {
    queryParams.append('page', _page.toString());
  }
  if (pageSize) {
    queryParams.append('size', pageSize.toString());
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${SUPPORT_TICKETS_ENDPOINTS.SEARCH}?${queryString}`
    : SUPPORT_TICKETS_ENDPOINTS.SEARCH;

  return useApiQuery<TicketsListResponse>(
    [
      'support-tickets',
      'search',
      searchQuery || '',
      page?.toString() || '0',
      pageSize?.toString() || '10',
      active !== undefined ? active.toString() : '',
    ],
    endpoint,
    {
      staleTime: 30 * 1000, // 30 seconds
      retry: 3,
      retryDelay: 1000,
      enabled: !!searchQuery, // Only run query if searchQuery is provided
      select: (data: TicketsListResponse) => ({
        ...data,
        hasMore: data.has_next,
        page: data.current_page,
        pageSize: data.page_size,
        total: data.total_count,
      }),
    } as any
  );
};

/**
 * Hook for fetching ticket detail by ID
 * @param ticketId - The ticket identifier
 */
export const useFetchTicketDetail = (ticketId: string) => {
  const endpoint = SUPPORT_TICKETS_ENDPOINTS.DETAIL(ticketId);

  return useApiQuery<TicketDetailResponse>(
    ['support-tickets', 'detail', ticketId],
    endpoint,
    {
      staleTime: 0,
      gcTime: 0,
      retry: 3,
      retryDelay: 1000,
      enabled: !!ticketId, // Only run query if ticketId is provided
    } as any
  );
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook for creating a new support ticket
 * Includes error transformation for 400, 409 response codes
 */
export const useCreateTicket = () => {
  return useFileUpload<CreateTicketResponse>(SUPPORT_TICKETS_ENDPOINTS.CREATE);
};

/**
 * Hook for uploading ticket attachments
 */
export const useUploadTicketAttachment = () => {
  return useFileUpload<UploadAttachmentsResponse>(
    SUPPORT_TICKETS_ENDPOINTS.UPLOAD_ATTACHMENTS()
  );
};

export const useAddComment = () => {
  const [isPending, setIsPending] = useState(false);

  return {
    mutate: (variables: AddCommentRequest, options?: any) => {
      setIsPending(true);
      const { ticketId, formData } = variables;
      const endpoint = SUPPORT_TICKETS_ENDPOINTS.ADD_COMMENT(ticketId);

      apiClient.axios
        .post<AddCommentResponse>(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          options?.onSuccess?.(response.data);
        })
        .catch((error) => {
          if (options?.onError) {
            options.onError(error);
          }
        })
        .finally(() => setIsPending(false));
    },
    mutateAsync: async (variables: AddCommentRequest) => {
      setIsPending(true);
      try {
        const { ticketId, formData } = variables;
        const endpoint = SUPPORT_TICKETS_ENDPOINTS.ADD_COMMENT(ticketId);

        const response = await apiClient.axios.post<AddCommentResponse>(
          endpoint,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        return response.data;
      } finally {
        setIsPending(false);
      }
    },
    isPending,
  };
};

/**
 * Hook for reopening a closed ticket
 */
export const useReopenTicket = () => {
  return useApiMutation<TicketDetailResponse, { ticketId: string }>(
    (variables) => SUPPORT_TICKETS_ENDPOINTS.REOPEN(variables.ticketId),
    'PATCH'
  );
};

/**
 * Hook for marking a ticket as read
 */
export const useMarkTicketAsRead = () => {
  return useApiMutation<void, { ticketId: string }>(
    (variables) => SUPPORT_TICKETS_ENDPOINTS.MARK_READ(variables.ticketId),
    'POST'
  );
};

/**
 * Hook for fetching dropdown options for ticket creation
 * This hook fetches the available options for Function (L1), Type (L2), and Service (L3) dropdowns
 * @param enabled - Whether to enable the query (default: true)
 */
export const useFetchDropdownOptions = (enabled: boolean = true) => {
  const { i18n } = useTranslation();

  return useApiQuery<DropdownOptionsResponse>(
    ['support-tickets', 'dropdown-options', i18n.language],
    SUPPORT_TICKETS_ENDPOINTS.DROPDOWN_OPTIONS,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - dropdown options don't change frequently
      gcTime: 10 * 60 * 1000, // 10 minutes cache time
      retry: 3,
      retryDelay: 1000,
      enabled, // Control whether the query should run
    } as any
  );
};

/**
 * TDF-1388/TDF-1390: fetches the Topic → Sub-Topic → Issue catalog feeding the
 * Sub-Topic/Issue cascade under "Oracle ERP Issues" and the single item under
 * "IT Services". Cache key includes language since labels come back localized.
 * @param enabled - Whether to enable the query (drawer-open gated, same as dropdown options)
 */
export const useFetchSupportIssues = (enabled: boolean = true) => {
  const { i18n } = useTranslation();

  return useApiQuery<SupportIssuesCatalogResponse>(
    ['support-tickets', 'support-issues', i18n.language],
    SUPPORT_TICKETS_ENDPOINTS.SUPPORT_ISSUES,
    {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
      enabled,
    } as any
  );
};

/**
 * Comprehensive custom hook for Support Tickets operations
 *
 * @deprecated Use individual React Query hooks instead (useCreateTicket, useAddComment, etc.)
 * This hook exists for backward compatibility with existing components
 */
export const useSupportTicketsApi = () => {
  const [error, setError] = useState<{ message: string } | null>(null);
  const [loading] = useState(false);

  // Use Zustand store for tickets state
  const { openTickets, closedTickets } = useSupportTicketsStore();

  /**
   * Clears any existing error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    error,
    loading,
    isSubmitting: loading,
    errorType: error ? 'error' : null,
    openTickets,
    closedTickets,

    // Utility functions
    clearError,
  };
};

/**
 * Custom hook for open tickets data management with infinite scroll support
 * Wraps useInfiniteOpenTicketsQuery to provide a cleaner interface similar to useInfiniteTeams
 * @param pageSize - Number of tickets to fetch per page (default: 10)
 * @param srAttemptId - Optional SR attempt UUID to filter tickets (service details tab)
 * @param enabled - Whether to enable the query (default: true)
 * @param topic - Optional topic filter (TDF-1351)
 * @param serviceId - Optional L3 service filter (TDF-1351)
 * @param keyword - Optional free-text search over number/topic/subject (TDF-1351 AC7)
 * @param status - Optional status filter, Open tab only (TDF-1394)
 */
export const useInfiniteOpenTickets = (
  pageSize: number = 10,
  srAttemptId?: string | undefined,
  enabled: boolean = true,
  topic?: string | undefined,
  serviceId?: string | undefined,
  keyword?: string | undefined,
  status?: string | undefined,
  bundleAttemptId?: string | undefined
) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteOpenTicketsQuery(
    pageSize,
    srAttemptId,
    enabled,
    topic,
    serviceId,
    keyword,
    status,
    bundleAttemptId
  );

  // Flatten all pages into a single array of tickets
  const tickets = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.tickets);
  }, [data?.pages]);

  // Get total count from the first page
  const totalCount = useMemo(() => {
    const firstPage = data?.pages?.[0];
    return firstPage?.total_count || 0;
  }, [data?.pages]);

  const loadTickets = async () => {
    await refetch();
  };

  return {
    // Data
    tickets,
    loading: isLoading,
    isLoadingMore: isFetchingNextPage,
    isFetching, // Exposed in case needed
    error: error?.message || null,
    hasMore: hasNextPage,
    totalCount,

    // Actions
    loadTickets,
    loadMore: fetchNextPage,
    refresh: refetch,
  };
};

/**
 * Custom hook for closed tickets data management with infinite scroll support
 * Wraps useInfiniteClosedTicketsQuery to provide a cleaner interface similar to useInfiniteTeams
 * @param pageSize - Number of tickets to fetch per page (default: 10)
 * @param srAttemptId - Optional SR attempt UUID to filter tickets (service details tab)
 * @param enabled - Whether to enable the query (default: true)
 * @param topic - Optional topic filter (TDF-1351)
 * @param serviceId - Optional L3 service filter (TDF-1351)
 * @param keyword - Optional free-text search over number/topic/subject (TDF-1351 AC7)
 */
export const useInfiniteClosedTickets = (
  pageSize: number = 10,
  srAttemptId?: string | undefined,
  enabled: boolean = true,
  topic?: string | undefined,
  serviceId?: string | undefined,
  keyword?: string | undefined,
  bundleAttemptId?: string | undefined
) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteClosedTicketsQuery(
    pageSize,
    srAttemptId,
    enabled,
    topic,
    serviceId,
    keyword,
    bundleAttemptId
  );

  // Flatten all pages into a single array of tickets
  const tickets = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.tickets);
  }, [data?.pages]);

  // Get total count from the first page
  const totalCount = useMemo(() => {
    const firstPage = data?.pages?.[0];
    return firstPage?.total_count || 0;
  }, [data?.pages]);

  const loadTickets = async () => {
    await refetch();
  };

  return {
    // Data
    tickets,
    loading: isLoading,
    isLoadingMore: isFetchingNextPage,
    isFetching, // Exposed in case needed
    error: error?.message || null,
    hasMore: hasNextPage,
    totalCount,

    // Actions
    loadTickets,
    loadMore: fetchNextPage,
    refresh: refetch,
  };
};
